import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import SysAdminService from "../../services/sysadmin";
import LocalstorageService from "../../helpers/localstorage-service";
import { CompanyId } from "../../constants";

const DEFAULT_PARAMS = {
  rowStatus: "",
  loadingRoundingSum: 5000,
  minimumSumForReduction: 2500,
  minimumPerDevice: 3000,
  dayFraction: 0.35,
  specialDaysAmount: 1.2,
  weeklySum1: "",
  factor1: "",
  weeklySum2: "",
  factor2: "",
  weeklySum3: "",
  factor3: "",
  manuallyCounted: 30,
  cleaningCardReader: 45,
};

function CompanyParameters() {
  const { t } = useTranslation();
  const [params, setParams] = useState({ ...DEFAULT_PARAMS });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const companyId = parseInt(LocalstorageService.getItem(CompanyId));

  useEffect(() => {
    loadParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParameters = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const res = await SysAdminService.getCompanyParameters(companyId);
      if (res.data?.status === 200 && res.data.data) {
        const d = res.data.data;
        setParams({
          rowStatus: d.rowStatus || "",
          loadingRoundingSum: d.loadingRoundingSum ?? 5000,
          minimumSumForReduction: d.minimumSumForReduction ?? 2500,
          minimumPerDevice: d.minimumPerDevice ?? 3000,
          dayFraction: d.dayFraction ?? 0.35,
          specialDaysAmount: d.specialDaysAmount ?? 1.2,
          weeklySum1: d.weeklySum1 ?? "",
          factor1: d.factor1 ?? "",
          weeklySum2: d.weeklySum2 ?? "",
          factor2: d.factor2 ?? "",
          weeklySum3: d.weeklySum3 ?? "",
          factor3: d.factor3 ?? "",
          manuallyCounted: d.manuallyCounted ?? 30,
          cleaningCardReader: d.cleaningCardReader ?? 45,
        });
      }
    } catch (error) {
      toast.error(t("ADMIN.PARAMS_LOAD_ERROR") || "שגיאה בטעינת פרמטרים");
    } finally {
      setIsLoading(false);
    }
  };

  const handleParamChange = (field, value) => {
    setParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await SysAdminService.saveCompanyParameters({
        companyId,
        ...params,
        weeklySum1: params.weeklySum1 === "" ? null : parseFloat(params.weeklySum1),
        factor1: params.factor1 === "" ? null : parseFloat(params.factor1),
        weeklySum2: params.weeklySum2 === "" ? null : parseFloat(params.weeklySum2),
        factor2: params.factor2 === "" ? null : parseFloat(params.factor2),
        weeklySum3: params.weeklySum3 === "" ? null : parseFloat(params.weeklySum3),
        factor3: params.factor3 === "" ? null : parseFloat(params.factor3),
      });
      if (res.data?.status === 200) {
        toast.success(t("ADMIN.PARAMS_SAVED") || "פרמטרים נשמרו בהצלחה");
      } else {
        toast.error(res.data?.message || "שגיאה בשמירה");
      }
    } catch (error) {
      toast.error(t("ADMIN.PARAMS_SAVE_ERROR") || "שגיאה בשמירת פרמטרים");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content-inner">
      <h4 className="mb-4">
        <i className="ri-settings-3-line me-2"></i>
        {t("ADMIN.COMPANY_PARAMETERS") || "פרמטרי חישוב"}
      </h4>

      <Card>
        <CardBody>
          <h5 className="mb-3">{t("SYSADMIN.PARAMS_TITLE") || "פרמטרי חישוב"}</h5>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.LINE_STATUS") || "סטטוס קו"}</Label>
                <Input value={params.rowStatus} onChange={(e) => handleParamChange("rowStatus", e.target.value)} />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.LOADING_ROUNDING") || "עיגול סכום טעינה"}</Label>
                <Input type="number" value={params.loadingRoundingSum} onChange={(e) => handleParamChange("loadingRoundingSum", parseFloat(e.target.value) || 0)} />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.MIN_REDUCTION") || "מינימום להפחתה"}</Label>
                <Input type="number" value={params.minimumSumForReduction} onChange={(e) => handleParamChange("minimumSumForReduction", parseFloat(e.target.value) || 0)} />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.MIN_PER_DEVICE") || "מינימום למכשיר"}</Label>
                <Input type="number" value={params.minimumPerDevice} onChange={(e) => handleParamChange("minimumPerDevice", parseFloat(e.target.value) || 0)} />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.DAY_FRACTION") || "חלק היום"}</Label>
                <Input type="number" step="0.01" value={params.dayFraction} onChange={(e) => handleParamChange("dayFraction", parseFloat(e.target.value) || 0)} />
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.SPECIAL_DAYS_AMOUNT") || "מקדם ימים מיוחדים"}</Label>
                <Input type="number" step="0.01" value={params.specialDaysAmount} onChange={(e) => handleParamChange("specialDaysAmount", parseFloat(e.target.value) || 0)} />
              </div>
            </Col>
          </Row>

          <h6 className="mt-3 mb-2">{t("SYSADMIN.MAINTENANCE_PARAMS") || "פרמטרי תחזוקה"}</h6>
          <Row>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.MANUALLY_COUNTED") || "ספירה ידנית (ימים)"}</Label>
                <Input type="number" value={params.manuallyCounted} onChange={(e) => handleParamChange("manuallyCounted", parseInt(e.target.value) || 0)} />
                <small className="text-muted">{t("SYSADMIN.MANUALLY_COUNTED_DESC") || "מספר ימים בין ספירות ידניות של מסופים"}</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <Label>{t("SYSADMIN.CLEANING_CARD_READER") || "ניקוי קורא כרטיסים (ימים)"}</Label>
                <Input type="number" value={params.cleaningCardReader} onChange={(e) => handleParamChange("cleaningCardReader", parseInt(e.target.value) || 0)} />
                <small className="text-muted">{t("SYSADMIN.CLEANING_CARD_READER_DESC") || "מספר ימים בין ניקויי קורא כרטיסים"}</small>
              </div>
            </Col>
          </Row>

          <h6 className="mt-3 mb-2">{t("SYSADMIN.WEEKLY_FACTORS") || "מקדמים שבועיים"}</h6>
          <small className="text-muted d-block mb-2">{t("SYSADMIN.WEEKLY_FACTORS_DESC") || "סכום שבועי מינימלי והמקדם שיחול כאשר הסכום עובר את הסף"}</small>
          <Row>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 1</Label><Input type="number" value={params.weeklySum1} onChange={(e) => handleParamChange("weeklySum1", e.target.value)} /></div></Col>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 1</Label><Input type="number" step="0.01" value={params.factor1} onChange={(e) => handleParamChange("factor1", e.target.value)} /></div></Col>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 2</Label><Input type="number" value={params.weeklySum2} onChange={(e) => handleParamChange("weeklySum2", e.target.value)} /></div></Col>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 2</Label><Input type="number" step="0.01" value={params.factor2} onChange={(e) => handleParamChange("factor2", e.target.value)} /></div></Col>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.WEEKLY_SUM") || "סכום שבועי"} 3</Label><Input type="number" value={params.weeklySum3} onChange={(e) => handleParamChange("weeklySum3", e.target.value)} /></div></Col>
            <Col md={2}><div className="mb-3"><Label>{t("SYSADMIN.FACTOR") || "מקדם"} 3</Label><Input type="number" step="0.01" value={params.factor3} onChange={(e) => handleParamChange("factor3", e.target.value)} /></div></Col>
          </Row>

          <Button color="primary" onClick={handleSave} disabled={isSaving} className="mt-3">
            {isSaving ? <Spinner size="sm" /> : (
              <>
                <i className="ri-save-line me-1"></i>
                {t("SUBMIT") || "שמור"}
              </>
            )}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}

export default CompanyParameters;
